import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from 'react-toastify';
import { useAuth } from "../../auth/AuthContext";
import primaryBackground from "../../assets/Backgrounds/primaryBg.svg";
import LoadingPage from "../../Loader";
import Footer from "../home/Footer";
import { Box, TextField, Button, Typography } from "@mui/material";
import ActionButton from '../../ActionButton';
import ReCAPTCHA from "react-google-recaptcha";

const ContactUs = () => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '', 
        lastName: '', 
        email: '', 
        subject: '', 
        message: '' 
    });
    const [errors, setErrors] = useState({});
    const { sendMails, getData } = useAuth();
    const [background, setBackground] = useState({});
    const [captchaValue, setCaptchaValue] = useState(null);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const response = await getData('proccess.php', 'backgrounds');
                if (response.success) {
                    const contactbg = response.data.find(bg => bg.page_name === 'contact');
                    if (contactbg) {
                        setBackground(contactbg);
                    }
                }
            } catch (error) {
                setErrors({ general: error.message });
            } finally {
                setLoading(false);
            }
        }
        
        fetchData();
    }, [getData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field on change
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            newErrors.email = 'Valid email is required';
        }
        if (!formData.subject.trim()) {
            newErrors.subject = 'Subject is required';
        }
        if (!formData.message.trim()) {
            newErrors.message = 'Message is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        if (!captchaValue) {
            toast.error('Please verify that you are not a robot');
            return;
        }

        setLoading(true);
        try {
            const response = await sendMails('proccess.php', { 
                firstName: formData.firstName, 
                lastName: formData.lastName,
                email: formData.email, 
                subject: formData.subject,
                message: formData.message, 
                captcha: captchaValue 
            });
            if (response.success) {
                alert(response.message || 'Email sent successfully.');
                setFormData({ firstName: '', lastName: '', email: '', subject: '', message: '' });
            } else {
                alert(response.message || 'Submission failed');
            }
        } catch (error) {
            alert(`Submission error: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <LoadingPage />;

    return (
        <div className="parentContainer">
            <ToastContainer 
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick={false} // Change this to false
                rtl={false}
                pauseOnFocusLoss
                draggable={false} // Change this to false
                pauseOnHover
                limit={3} // Add a limit to prevent too many toasts
            />
            <div className="primary-background" style={{
                backgroundImage: `url(${primaryBackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            }}></div>

            <Box sx={{
                minHeight: '65vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'start',
            }}>
                <Box width={'100%'} display={'flex'} alignItems={'center'} justifyContent={'center'}
                    sx={{
                        backgroundImage: `url(https://mindsinaction.com.na/assets/media/pictures/${background.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                    }}
                >
                    <Box width={'100%'} display={'flex'} alignItems={'center'} justifyContent={'center'}
                        sx={{ backgroundColor: '#ffffffc9' }}
                    >
                        <Box width={'100%'} maxWidth={'1200px'} padding={'1rem'} marginTop={'4rem'} 
                            textAlign={'center'}
                        >
                            <h1
                                className="primary-header"
                                style={{ color: "#000000ff", margin: "2rem 0 2rem 0" }}
                            >
                                Contact Us
                            </h1>
                            <p style={{ color: '#000000c9', fontSize: '1.2rem' }}>
                                We are located in Suiderhof, No. 6, Andimba Toivo-ya-Toivo street. In Huster Machinetools building first floor, opposite CTM. Please contact us before your visit.
                            </p>
                        </Box>
                    </Box>
                </Box>

                {/* Contact form */}
                <Box sx={{ 
                    width: '100%', 
                    maxWidth: {xs: '350px', sm: '600px'}, 
                    padding: '2rem', 
                    margin: '2rem auto', 
                    backgroundColor: '#3b76b2',
                    borderRadius: '2rem'
                }}>
                    <Typography variant="h4" align="center" 
                    gutterBottom sx={{ color: '#ffffffff', mb: 3 }} fontWeight={700}>
                        Send Us An Email
                    </Typography>
                    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box display={'flex'} gap={1}>
                            <TextField
                                name="firstName"
                                label="First Name"
                                value={formData.firstName}
                                onChange={handleChange}
                                error={!!errors.firstName}
                                helperText={errors.firstName}
                                required
                                fullWidth
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'transparent',
                                        color: 'white',
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: 'white',
                                    },
                                    '& .MuiFormHelperText-root': {
                                        color: 'white',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'white',
                                    },
                                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'white',
                                    },
                                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#90ca25',
                                    },
                                }}
                            />
                            <TextField
                                name="lastName"
                                label="Last Name"
                                value={formData.lastName}
                                onChange={handleChange}
                                error={!!errors.lastName}
                                helperText={errors.lastName}
                                required
                                fullWidth
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'transparent',
                                        color: 'white',
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: 'white',
                                    },
                                    '& .MuiFormHelperText-root': {
                                        color: 'white',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'white',
                                    },
                                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'white',
                                    },
                                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#90ca25',
                                    },
                                }}
                            />
                        </Box>
                        
                        <Box display={'flex'} gap={1} flexWrap={'wrap'}>
                            <TextField
                                name="email"
                                label="Email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                error={!!errors.email}
                                helperText={errors.email}
                                required
                                fullWidth
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'transparent',
                                        color: 'white',
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: 'white',
                                    },
                                    '& .MuiFormHelperText-root': {
                                        color: 'white',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'white',
                                    },
                                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'white',
                                    },
                                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#90ca25',
                                    },
                                }}
                            />
                            <TextField
                                name="subject"
                                label="Subject"
                                value={formData.subject}
                                onChange={handleChange}
                                error={!!errors.subject}
                                helperText={errors.subject}
                                required
                                fullWidth
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'transparent',
                                        color: 'white',
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: 'white',
                                    },
                                    '& .MuiFormHelperText-root': {
                                        color: 'white',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'white',
                                    },
                                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'white',
                                    },
                                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#90ca25',
                                    },
                                }}
                            />
                        </Box>
                        <TextField
                            name="message"
                            label="Message"
                            multiline
                            rows={4}
                            value={formData.message}
                            onChange={handleChange}
                            error={!!errors.message}
                            helperText={errors.message}
                            required
                            fullWidth
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'transparent',
                                    color: 'white',
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'white',
                                },
                                '& .MuiFormHelperText-root': {
                                    color: 'white',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'white',
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'white',
                                },
                                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#90ca25',
                                },
                            }}
                        />
                        <div className="form-group">
                            <ReCAPTCHA
                                sitekey="6Lez9o8sAAAAAO0869BEv2GU4dOrPge7E-VbV4IQ"
                                onChange={(value) => setCaptchaValue(value)}
                            />
                        </div>
                        <Box width={'100%'} display={'flex'} justifyContent={'end'}>
                            <ActionButton btnType={'submit'} tittle={loading ? 'Sending...' : 'Send Message'} btnwidth="160px"/>
                        </Box>
                    </Box>
                </Box>
            </Box>
            {/* Footer */}
            <Footer />
        </div>
    );
};

export default ContactUs;